import { useEffect, useRef, useState } from "react";
import { LocaleLink as Link } from "@/components/landing/LocaleLink";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import ModulePicture from "./ModulePicture";
import Screenshot from "./Screenshot";
import { FEATURES } from "./data";
import { MARKETING_PAGES } from "./flags";
import type { FeatureMeta } from "./data";

type VisState = "below" | "visible" | "above";

/** Una fila por feature. Usa IntersectionObserver propio para saber si
 *  está debajo del viewport, dentro o ya por arriba — esos 3 estados se
 *  reflejan como clases `is-below|is-visible|is-above` que el CSS usa
 *  para deslizarla por la izquierda o derecha según el index. */
function FeatureRow({
  feature,
  index,
  eyebrow,
  startVisible = false,
}: {
  feature: FeatureMeta;
  index: number;
  eyebrow: string;
  /** Arranca ya colocada, sin animación de entrada. Para las filas que
   *  aparecen al pulsar "Descubre más": el estado `below` las deja
   *  desplazadas 120 px en horizontal, y como el contenido ya roza el ancho
   *  del viewport, la fila entraba visiblemente cortada por un lado hasta
   *  que el observer la corregía. Lo que el visitante pide ver
   *  explícitamente no debería llegar a medio salir de la pantalla. */
  startVisible?: boolean;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<VisState>(startVisible ? "visible" : "below");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Las reveladas a mano no se observan: volverían a salir por el lado al
    // pasar de largo (`is-above` también desplaza en horizontal).
    if (startVisible) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setState("visible");
        } else if (entry.boundingClientRect.top < 0) {
          setState("above");
        } else {
          setState("below");
        }
      },
      {
        threshold: 0,
        // Encogemos la zona de detección un 20 % arriba y abajo: la
        // animación de entrada arranca cuando la fila ya está bastante
        // metida en el viewport (no apenas asomando), y la de salida no
        // se dispara hasta que la fila está casi fuera por arriba.
        rootMargin: "-20% 0px -20% 0px",
      },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [startVisible]);

  const Icon = feature.icon;
  const label = t(`features.${feature.key}.label`);
  const descLong = t(`features.${feature.key}.descLong`);
  const bullets =
    (t(`features.${feature.key}.bullets`, { returnObjects: true }) as unknown as string[]) || [];
  const reversed = index % 2 !== 0;

  return (
    <div
      ref={ref}
      className={`playium-modules__row${reversed ? " is-reversed" : ""} is-${state}`}
    >
      {(feature.decos ?? []).map((d) => (
        <img loading="lazy"
          key={d.kind}
          src={d.src}
          alt=""
          aria-hidden="true"
          className={`playium-modules__row-deco playium-modules__row-deco--${d.kind}`}
        />
      ))}

      <div className="playium-modules__shot">
        {feature.shot ? (
          // LDT-717 · Todas diferidas: la galería queda muy por debajo del
          // primer pantallazo, y las dos primeras en `eager` eran 2 MB que
          // competían con el `h1` (el LCP). `sizes` sale de medir el ancho
          // pintado: todo el contenedor en móvil, 640 px en tableta y
          // ~724 px como mucho en escritorio.
          <Screenshot
            shot={feature.shot}
            alt={t("features.shotAlt", { label })}
            className="playium-modules__shot-img"
            sizes="(max-width: 767px) calc(100vw - 48px), (max-width: 1023px) 640px, 724px"
          />
        ) : (
          <ModulePicture
            slug={feature.key}
            eyebrow={eyebrow}
            label={label}
            eager={index < 2}
          />
        )}
      </div>

      <div className="playium-modules__content">
        <div className="playium-modules__feature-head">
          {feature.icon3d ? (
            <img loading="lazy"
              src={feature.icon3d}
              alt=""
              aria-hidden="true"
              className="playium-modules__icon3d"
            />
          ) : (
            <span className="playium-modules__icon-fallback" aria-hidden="true">
              <Icon />
            </span>
          )}
          <h3 className="playium-modules__feature-title playium-heading">
            {label}
          </h3>
        </div>

        <p className="playium-modules__desc">{descLong}</p>

        <ul className="playium-modules__bullets">
          {bullets.slice(0, 3).map((b, bi) => (
            <li key={bi} className="playium-modules__bullet">
              <Check aria-hidden="true" className="playium-modules__check" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        {/* "Ver más" lleva a /features#<key>; mientras esa página esté oculta
            (ver flags.ts) no mostramos el enlace. */}
        {MARKETING_PAGES.features && (
          <Link
            to={`/features#${feature.key}`}
            onClick={() =>
              trackEvent("module_clicked", {
                module_key: feature.key,
                location: "hub_gallery",
              })
            }
            className="playium-modules__link"
          >
            {t("features.viewMore")}
            <ArrowRight aria-hidden="true" />
          </Link>
        )}
      </div>
    </div>
  );
}

// Cuántas features se muestran de entrada. El resto se revelan una a una con el
// botón "Descubre más" (la sección era muy larga mostrando las 10 de golpe).
const INITIAL_VISIBLE = 3;

export default function ModulesGallery() {
  const { t } = useTranslation();
  const eyebrow = t("features.eyebrow");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const rowsRef = useRef<HTMLDivElement>(null);

  const revealNext = () => {
    const newIndex = visibleCount; // índice (0-based) de la fila que va a aparecer
    setVisibleCount((c) => Math.min(c + 1, FEATURES.length));
    // Tras el render, scroll suave a la nueva fila (dispara su animación de entrada).
    setTimeout(() => {
      const el = rowsRef.current?.children[newIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  return (
    <section id="features" className="playium-modules">
      <div className="playium-modules__inner">
        <div className="playium-modules__header">
          <h2 className="playium-modules__title playium-heading">
            {t("features.title")}
          </h2>
          <p className="playium-modules__subtitle">
            {t("features.subtitle")}
          </p>
        </div>

        <div className="playium-modules__rows" ref={rowsRef}>
          {FEATURES.slice(0, visibleCount).map((feature, i) => (
            <FeatureRow
              key={feature.key}
              feature={feature}
              index={i}
              eyebrow={eyebrow}
              startVisible={i >= INITIAL_VISIBLE}
            />
          ))}
        </div>

        {visibleCount < FEATURES.length && (
          <div className="flex justify-center mt-10">
            <button
              type="button"
              onClick={revealNext}
              className="inline-flex items-center gap-2 rounded-full bg-[#37AD9F] hover:bg-[#2C9384] text-white font-semibold px-8 py-3 shadow-lg shadow-[#37AD9F]/30 transition-colors"
            >
              {t("features.discoverMore")}
              <ChevronDown aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
